/**
 * Hook for calendar view navigation with URL state persistence.
 *
 * Syncs view mode and current date to URL search params (?view=&date=)
 * so that browser back/forward and deep-linking work correctly.
 *
 * @module hooks/useCalendarNavigation
 */

import { useMemo, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  parseISO,
  isValid,
  format,
  startOfYear,
  endOfYear,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  addYears,
  subYears,
} from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

const VALID_VIEWS = ['day', 'week', 'month', 'year', 'schedule']

function parseViewParam(raw) {
  return VALID_VIEWS.includes(raw) ? raw : null
}

function parseDateParam(raw) {
  if (!raw) return null
  try {
    const d = parseISO(raw)
    return isValid(d) ? d : null
  } catch {
    return null
  }
}

/**
 * @param {string} userTz - IANA timezone identifier
 * @param {string} [defaultView='month']
 * @returns {{
 *   viewMode: string,
 *   setViewMode: (mode: string) => void,
 *   currentDate: Date,
 *   dateRange: { start: Date, end: Date },
 *   title: string,
 *   goToday: () => void,
 *   goNext: () => void,
 *   goPrev: () => void,
 *   goToDate: (date: Date) => void
 * }}
 */
export function useCalendarNavigation(userTz, defaultView = 'month') {
  const [searchParams, setSearchParams] = useSearchParams()

  const viewMode = parseViewParam(searchParams.get('view')) || defaultView
  const dateParam = searchParams.get('date')
  const currentDate = useMemo(
    () => parseDateParam(dateParam) || utcToZonedTime(new Date(), userTz),
    [dateParam, userTz]
  )

  // Update URL without adding to browser history
  const updateParams = useCallback(
    (view, date) => {
      setSearchParams(
        { view, date: format(date, 'yyyy-MM-dd') },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  // Seed URL with preferred view on first mount if no params present
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current) return
    seeded.current = true
    if (!searchParams.get('view')) {
      setSearchParams(
        { view: defaultView, date: format(currentDate, 'yyyy-MM-dd') },
        { replace: true }
      )
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const setViewMode = useCallback(
    (mode) => {
      if (VALID_VIEWS.includes(mode)) updateParams(mode, currentDate)
    },
    [currentDate, updateParams]
  )

  const goToDate = useCallback(
    (date) => updateParams(viewMode, date),
    [viewMode, updateParams]
  )

  const goToday = useCallback(
    () => {
      const todayInTz = utcToZonedTime(new Date(), userTz)
      updateParams(viewMode, todayInTz)
    },
    [viewMode, updateParams, userTz]
  )

  const goNext = useCallback(() => {
    let next
    switch (viewMode) {
      case 'day':
        next = addDays(currentDate, 1)
        break
      case 'week':
        next = addWeeks(currentDate, 1)
        break
      case 'month':
        next = addMonths(currentDate, 1)
        break
      case 'year':
        next = addYears(currentDate, 1)
        break
      case 'schedule':
        next = addMonths(currentDate, 1)
        break
      default:
        next = addMonths(currentDate, 1)
    }
    updateParams(viewMode, next)
  }, [viewMode, currentDate, updateParams])

  const goPrev = useCallback(() => {
    let prev
    switch (viewMode) {
      case 'day':
        prev = subDays(currentDate, 1)
        break
      case 'week':
        prev = subWeeks(currentDate, 1)
        break
      case 'month':
        prev = subMonths(currentDate, 1)
        break
      case 'year':
        prev = subYears(currentDate, 1)
        break
      case 'schedule':
        prev = subMonths(currentDate, 1)
        break
      default:
        prev = subMonths(currentDate, 1)
    }
    updateParams(viewMode, prev)
  }, [viewMode, currentDate, updateParams])

  // Compute fetch date range (timezone-aware)
  // currentDate is already in user's timezone (from parseDateParam or utcToZonedTime above)
  const dateRange = useMemo(() => {
    const nowInTz = currentDate

    switch (viewMode) {
      case 'day': {
        const ds = zonedTimeToUtc(startOfDay(nowInTz), userTz)
        const de = zonedTimeToUtc(endOfDay(nowInTz), userTz)
        return { start: subDays(ds, 1), end: addDays(de, 1) }
      }
      case 'week': {
        const ws = zonedTimeToUtc(startOfWeek(nowInTz), userTz)
        const we = zonedTimeToUtc(endOfWeek(nowInTz), userTz)
        return { start: subDays(ws, 1), end: addDays(we, 1) }
      }
      case 'month':
      case 'schedule': {
        const ms = startOfMonth(subMonths(nowInTz, 1))
        const me = endOfMonth(addMonths(nowInTz, 1))
        return {
          start: zonedTimeToUtc(ms, userTz),
          end: zonedTimeToUtc(me, userTz),
        }
      }
      case 'year': {
        const ys = startOfYear(nowInTz)
        const ye = endOfYear(nowInTz)
        return {
          start: zonedTimeToUtc(ys, userTz),
          end: zonedTimeToUtc(ye, userTz),
        }
      }
      default: {
        const ms2 = startOfMonth(subMonths(nowInTz, 1))
        const me2 = endOfMonth(addMonths(nowInTz, 1))
        return {
          start: zonedTimeToUtc(ms2, userTz),
          end: zonedTimeToUtc(me2, userTz),
        }
      }
    }
  }, [viewMode, currentDate, userTz])

  // Toolbar title
  const title = useMemo(() => {
    const d = utcToZonedTime(currentDate, userTz)
    switch (viewMode) {
      case 'day':
        return format(d, 'EEEE, MMMM d, yyyy')
      case 'week': {
        const ws = startOfWeek(d)
        const we = endOfWeek(d)
        const sameMonth = format(ws, 'MMM') === format(we, 'MMM')
        if (sameMonth) return `${format(ws, 'MMM d')} – ${format(we, 'd, yyyy')}`
        return `${format(ws, 'MMM d')} – ${format(we, 'MMM d, yyyy')}`
      }
      case 'month':
        return format(d, 'MMMM yyyy')
      case 'year':
        return format(d, 'yyyy')
      case 'schedule':
        return format(d, 'MMMM yyyy')
      default:
        return format(d, 'MMMM yyyy')
    }
  }, [viewMode, currentDate, userTz])

  return {
    viewMode,
    setViewMode,
    currentDate,
    dateRange,
    title,
    goToday,
    goNext,
    goPrev,
    goToDate,
  }
}

export default useCalendarNavigation
